import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { SIGNS } from '../../data/astro/signs';
import { BIG_THREE } from '../../data/astro/bigThree';
import BirthChartTool from './BirthChartTool';
import './BirthChart.css';

const PAGES = {
  'big-three': {
    path: '/big-three-calculator/',
    track: 'big-three-calculator',
    title: 'Big Three Calculator: Sun, Moon and Rising Sign | NowTarot',
    description: 'Free Big Three calculator. Find your Sun, Moon, and Rising sign from your birth date, time, and place, with what each one means and a full birth chart reading.',
    appName: 'Free Big Three Calculator',
    aboutTitle: <>What is your <em>Big Three</em>?</>,
    about: [
      'Your Big Three are your Sun, Moon, and Rising signs. Astrologers start with them because together they cover the most ground: who you are at the core, what you need to feel okay, and how other people meet you.',
      'Your Sun sign is the one you already know, set by your birthday. Your Moon sign depends on the day and roughly the hour, because the Moon changes signs every two to three days. Your Rising sign, also called the ascendant, changes about every two hours, so it needs your birth time and the city you were born in.',
      'That is why two people with the same Sun sign can feel so different. A Leo Sun with a Cancer Moon and Virgo Rising lives a very different life from a Leo Sun with an Aries Moon and Leo Rising.',
    ],
    points: [
      { title: 'Sun: your core', text: 'Your identity, your will, and what makes you feel most like yourself.' },
      { title: 'Moon: your needs', text: 'Your feelings, your instincts, and what you need to feel safe and loved.' },
      { title: 'Rising: your approach', text: 'Your first impression, your body language, and how you start things.' },
    ],
    signsRole: 'Sun',
    signsTitle: 'Sun signs at a glance',
    faq: [
      { question: 'How do I find my Big Three?', answer: 'Enter your birth date, birth time, and birth city above. The calculator works out your Sun, Moon, and Rising signs from the positions of the planets at that moment, then draws your full chart.' },
      { question: 'Can I find my Big Three without a birth time?', answer: 'You can find your Sun sign and usually your Moon sign. The Rising sign needs a birth time. If the Moon changed signs on your birthday, we show both possible Moon signs.' },
      { question: 'Which of the Big Three matters most?', answer: 'None of them outranks the others. The Sun is your core, the Moon is your inner life, and the Rising sign is your outer style. Many people relate most to their Moon or Rising sign.' },
      { question: 'Is this Big Three calculator free?', answer: 'Yes. Your Big Three, the full chart, the reading, and questions about it are free, with no account needed.' },
    ],
  },
  rising: {
    path: '/rising-sign-calculator/',
    track: 'rising-sign-calculator',
    title: 'Rising Sign Calculator: Find Your Ascendant | NowTarot',
    description: 'Free rising sign calculator. Enter your birth date, time, and place to find your ascendant, what it says about you, and your full birth chart reading.',
    appName: 'Free Rising Sign Calculator',
    aboutTitle: <>What is a <em>rising sign</em>?</>,
    about: [
      'Your rising sign, or ascendant, is the zodiac sign that was rising on the eastern horizon at the exact moment you were born. It describes how you come across, your first instinct in a new situation, and the style you meet the world with.',
      'Because the Earth turns, a new sign rises about every two hours. That means your rising sign needs your birth time and your birthplace. Even 15 minutes can matter if you were born close to a change.',
      'Your rising sign also sets up the twelve houses of your chart, so it decides which parts of life each planet touches. It is the reason astrologers ask for a birth time.',
    ],
    points: [
      { title: 'First impressions', text: 'How strangers see you before they know you.' },
      { title: 'Your approach', text: 'How you start things, enter rooms, and handle the new.' },
      { title: 'Your chart\'s frame', text: 'The sign on the first house, which sets every other house.' },
    ],
    signsRole: 'Ascendant',
    signsTitle: 'The twelve rising signs',
    faq: [
      { question: 'How do I calculate my rising sign?', answer: 'Enter your birth date, exact birth time, and birth city. The calculator finds the sign on the eastern horizon at that moment, which is your ascendant.' },
      { question: 'What if I don\'t know my birth time?', answer: 'Without a time, the rising sign can\'t be found. A birth certificate, hospital record, or a parent may have it. You still get your Sun, Moon, and other planets in their signs.' },
      { question: 'Is the rising sign the same as the ascendant?', answer: 'Yes. Rising sign and ascendant are two names for the same point.' },
      { question: 'Why doesn\'t my rising sign match my personality?', answer: 'The rising sign is your outer style, not your whole self. Your Sun and Moon still shape who you are underneath. A wrong birth time can also give the wrong rising sign.' },
    ],
  },
  moon: {
    path: '/moon-sign-calculator/',
    track: 'moon-sign-calculator',
    title: 'Moon Sign Calculator: Find Your Moon Sign | NowTarot',
    description: 'Free moon sign calculator. Find your moon sign from your birth date and time, what it says about your emotions and needs, and your full birth chart reading.',
    appName: 'Free Moon Sign Calculator',
    aboutTitle: <>What is a <em>moon sign</em>?</>,
    about: [
      'Your moon sign is the sign the Moon was in when you were born. Where your Sun sign is the self you show, your Moon sign is the self you feel: your moods, your instincts, and what you need to feel safe and cared for.',
      'The Moon moves fast. It spends only two to three days in each sign, so your moon sign depends on your birth date and, on the days it changes signs, on your birth time.',
      'If you don\'t know your time, the calculator tells you whether the Moon changed signs on your birthday and shows both options, so you can read both and see which one fits.',
    ],
    points: [
      { title: 'Emotions', text: 'How you feel things and how you show it.' },
      { title: 'Needs', text: 'What makes you feel safe, loved, and at home.' },
      { title: 'Instincts', text: 'How you react before you have time to think.' },
    ],
    signsRole: 'Moon',
    signsTitle: 'The twelve moon signs',
    faq: [
      { question: 'How do I find my moon sign?', answer: 'Enter your birth date, and your birth time and city if you know them. The calculator places the Moon at your birth moment and draws your full chart.' },
      { question: 'Can I find my moon sign without a birth time?', answer: 'Usually, yes. On most days the Moon stays in one sign. On days it changes signs, we show both possibilities and explain that your birth time decides.' },
      { question: 'What is the difference between a sun sign and a moon sign?', answer: 'Your sun sign is your identity and drive. Your moon sign is your emotional nature and your needs. Both are part of you.' },
      { question: 'Is the moon sign calculator free?', answer: 'Yes. The moon sign, full chart, reading, and follow-up questions are free.' },
    ],
  },
};

const MORE = [
  { to: '/birth-chart/', label: 'Free birth chart', key: 'chart' },
  { to: '/big-three-calculator/', label: 'Big Three calculator', key: 'big-three' },
  { to: '/rising-sign-calculator/', label: 'Rising sign calculator', key: 'rising' },
  { to: '/moon-sign-calculator/', label: 'Moon sign calculator', key: 'moon' },
  { to: '/venus-sign-calculator/', label: 'Venus sign calculator', key: 'venus' },
  { to: '/birth-chart-compatibility/', label: 'Birth chart compatibility', key: 'synastry' },
  { to: '/zodiac-compatibility/', label: 'Zodiac compatibility', key: 'zodiac' },
  { to: '/moon-phase-today/', label: 'Moon phase today', key: 'moon-phase' },
];

const ChartLanding = ({ variant }) => {
  const page = PAGES[variant];
  const [drawn, setDrawn] = useState(false);

  const url = `https://nowtarot.com${page.path}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: page.appName,
        url,
        applicationCategory: 'LifestyleApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        description: page.description,
      },
      {
        '@type': 'FAQPage',
        mainEntity: page.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  };

  return (
    <div className="birth-page">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main className={drawn ? 'birth-wide' : ''}>
        <BirthChartTool variant={variant} onDrawnChange={setDrawn} />
        {!drawn && (
          <section className="birth-about">
            <h2>{page.aboutTitle}</h2>
            {page.about.map((text) => <p key={text}>{text}</p>)}
            <div className="birth-points">
              {page.points.map((point, index) => (
                <article key={point.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{point.title}</h3>
                  <p>{point.text}</p>
                </article>
              ))}
            </div>
            <h2>{page.signsTitle}</h2>
            <div className="birth-sign-list">
              {SIGNS.map((sign) => (
                <article key={sign.slug}>
                  <h3><span aria-hidden="true">{sign.glyph}</span> {sign.name}</h3>
                  <p>{BIG_THREE[page.signsRole][sign.name]}</p>
                </article>
              ))}
            </div>
            <div className="birth-faq">
              {page.faq.map((item) => (
                <article key={item.question}>
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </article>
              ))}
            </div>
            <nav className="birth-more" aria-label="More astrology tools">
              <h2>More free astrology</h2>
              <ul>
                {MORE.filter((item) => item.key !== variant).map((item) => (
                  <li key={item.to}><Link to={item.to}>{item.label}</Link></li>
                ))}
              </ul>
            </nav>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export { MORE };
export default ChartLanding;
